import * as React from 'react';
import {Mutation, Query} from 'react-apollo';
import {connect} from 'react-redux';

import {Assignables} from 'nutshell-core/assignables';
import * as Leads from 'nutshell-core/leads';
import * as Schema from 'nutshell-core/schema';
import {Session} from 'nutshell-core/session';
import type {Assignable} from 'nutshell-core/types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Sidebar} from 'shells/sidebar/sidebar';

import type {Comparator} from 'nutshell-graphql-types';
import type {
    GetLeadDistribution as GetLeadDistributionQuery,
    GetLeadDistributionVariables as GetLeadDistributionQueryVariables,
    CreateLeadDistributionAutomation as CreateLeadDistributionAutomationMutation,
    CreateLeadDistributionAutomationVariables as CreateLeadDistributionAutomationMutationVariables,
    CreateLeadDistributionAutomationInputFieldInput,
    UpdateLeadDistributionAutomation as UpdateLeadDistributionAutomationMutation,
    UpdateLeadDistributionAutomationVariables as UpdateLeadDistributionAutomationMutationVariables,
    UpdateLeadDistributionAutomationInputFieldInput,
    DeleteLeadDistributionAutomation as DeleteLeadDistributionAutomationMutation,
    DeleteLeadDistributionAutomationVariables as DeleteLeadDistributionAutomationMutationVariables,
    DeleteLeadDistributionAutomationInputFieldInput,
    StagesetsForPipelineList as StagesetsForPipelineListQuery,
} from 'nutshell-graphql-types';

import {SalesAutomationSidebarFormWrapper} from '../../../core/sidebar/sales-automation-sidebar-form-wrapper';

import {nutApolloClient} from '../../../../apollo-client';
import type {NutshellState} from '../../../../store';

import CREATE_LEAD_DISTRIBUTION_AUTOMATION from '../../graphql/mutations/create-lead-distribution-automation.graphql';
import DELETE_LEAD_DISTRIBUTION_AUTOMATION from '../../graphql/mutations/delete-lead-distribution-automation.graphql';
import UPDATE_LEAD_DISTRIBUTION_AUTOMATION from '../../graphql/mutations/update-lead-distribution-automation.graphql';
import GET_LEAD_DISTRIBUTION from '../../graphql/queries/get-lead-distribution.graphql';
import GET_STAGESETS from '../../graphql/queries/get-stagesets-for-pipeline-list.graphql';

import type {LeadDistributionRule} from './constants';
import {LeadDistributionForm} from './lead-distribution-form';

const EMPTY_RULE = {
    criteriaField: null,
    criteriaValues: [],
    valueComparator: 'EQUAL',
} as const;

type OwnProps = {
    onClose: () => any;
    stagesetId: string;
    // This exists if we have a current rule
    leadDistributionId: string | null | undefined;
};

type StateProps = {
    assignables: Assignable[];
    schema: Schema.Properties;
};

type State = {
    rule: LeadDistributionRule;
    hasInitialized: boolean;
};

type GraphQLProps = {
    leadDistributionRule: LeadDistributionRule | null | undefined;
    isLoading: boolean;
    createLeadDistributionAutomation: (
        arg1: CreateLeadDistributionAutomationInputFieldInput
    ) => Promise<any>;
    updateLeadDistributionAutomation: (
        arg1: UpdateLeadDistributionAutomationInputFieldInput
    ) => Promise<any>;
    deleteLeadDistributionAutomation: (
        arg1: DeleteLeadDistributionAutomationInputFieldInput
    ) => Promise<any>;
};

type Props = OwnProps & StateProps & GraphQLProps;

class LeadDistributionEditorComponent extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            // @ts-expect-error
            rule: EMPTY_RULE,
            hasInitialized: false,
        };
    }

    static getDerivedStateFromProps(props: Props, state: State) {
        if (state.hasInitialized) {
            return null;
        }

        if (!props.isLoading) {
            return {
                rule: props.leadDistributionRule || EMPTY_RULE,
                hasInitialized: true,
            };
        }

        return null;
    }

    render() {
        return (
            <Sidebar
                isOpen={true}
                headerTitle={
                    this.props.leadDistributionId
                        ? 'Edit lead distribution'
                        : 'Add lead distribution'
                }
                preventBodyScroll={false}
                canClickOutsideToClose={false}
                canEscapeToClose={true}
                onClose={this.props.onClose}
                onSave={this.onSave}
                isSaving={false}
                saveText={this.props.leadDistributionId ? 'Save' : 'Add'}
            >
                <SalesAutomationSidebarFormWrapper
                    id={this.props.leadDistributionId}
                    onDelete={{
                        deleteText: 'Remove distribution rule',
                        deleteConfirmationTitle:
                            'Are you sure you want to remove this lead distribution rule?',
                        onDeleteConfirm: this.handleDelete,
                    }}
                    onSaveForm={this.onSave}
                >
                    <LeadDistributionForm
                        isLoading={this.props.isLoading}
                        // @ts-expect-error
                        assignables={this.props.assignables}
                        rule={this.state.rule}
                        updateRule={this.updateRule}
                        schema={this.props.schema}
                    />
                </SalesAutomationSidebarFormWrapper>
            </Sidebar>
        );
    }

    handleDelete = (id: string): Promise<any> => {
        return this.props
            .deleteLeadDistributionAutomation({
                id: id,
            })
            .then(() => {
                this.props.onClose();
            });
    };

    updateRule = (newRule: LeadDistributionRule) => {
        this.setState({
            rule: newRule,
        });
    };

    onSave = () => {
        const {criteriaField, valueComparator, criteriaValues} = this.state.rule;

        if (criteriaField && criteriaValues) {
            const conditions = [
                {
                    config: {
                        filter: formatCriteria(criteriaField, valueComparator, criteriaValues),
                    },
                },
            ];

            if (this.props.leadDistributionId) {
                // If we already have an existing lead distribution item, we need
                // to update the existing
                this.props
                    .updateLeadDistributionAutomation({
                        id: this.props.leadDistributionId,
                        // @ts-expect-error
                        conditions: conditions,
                    })
                    .then(() => {
                        this.props.onClose();
                    });
            } else {
                this.props
                    .createLeadDistributionAutomation({
                        stagesetId: this.props.stagesetId,
                        // @ts-expect-error
                        conditions: conditions,
                    })
                    .then(() => {
                        this.props.onClose();
                    });
            }
        }
    };
}

function formatCriteria(field: string, comparator: Comparator, values: string[]) {
    if (field === 'value') {
        return {
            fieldName: field,
            fieldType: 'CURRENCY',
            currencyConfig: {
                currencyComparator: comparator,
                currencyValue: Number(values[0]),
                currencyCode: Session.getInstanceDefaultCurrencyCode(),
            },
        };
    }

    if (field === 'emailDomain') {
        return {
            fieldName: field,
            fieldType: 'BOOLEAN',
            booleanConfig: {
                booleanValue: values[0] === 'true',
            },
        };
    }

    return {
        fieldName: field,
        fieldType: 'RELATIONSHIP',
        relationshipConfig: {
            relationshipData: values,
            relationshipAnyAll: 'ANY',
        },
    };
}

function mapStateToProps(state: NutshellState): StateProps {
    return {
        // @ts-expect-error
        assignables: Assignables.getAllEnabled(state),
        schema: Leads.getLeadsSchemaPropertyMap(state),
    };
}

// @ts-expect-error
const withRedux = connect<Props, OwnProps & GraphQLProps, _, _, _, _>(mapStateToProps);

class GetLeadDistribution extends Query<
    GetLeadDistributionQuery,
    GetLeadDistributionQueryVariables
> {}

class CreateMutation extends Mutation<
    CreateLeadDistributionAutomationMutation,
    CreateLeadDistributionAutomationMutationVariables
> {}
class UpdateMutation extends Mutation<
    UpdateLeadDistributionAutomationMutation,
    UpdateLeadDistributionAutomationMutationVariables
> {}
class DeleteMutation extends Mutation<
    DeleteLeadDistributionAutomationMutation,
    DeleteLeadDistributionAutomationMutationVariables
> {}

const ConnectedLeadDistributionEditor = withRedux(LeadDistributionEditorComponent);

export const LeadDistributionEditor = (props: OwnProps) => (
    <GetLeadDistribution
        query={GET_LEAD_DISTRIBUTION}
        variables={{id: props.leadDistributionId || ''}}
        skip={!props.leadDistributionId}
    >
        {({data, loading, error}) => {
            const leadDistributionRule =
                data && data.leadDistributionAutomation
                    ? formatLeadDistributionRule(data.leadDistributionAutomation)
                    : null;
            // @ts-expect-error
            const errorMessage = safelyGetGraphQLErrorMessage(error);

            // If we couldn't find an automation, log to trackjs and the user will
            // get a blank form to save a new one if they want. No need to let them know.
            // @ts-expect-error
            if (errorMessage && typeof window.trackJs !== 'undefined') {
                // @ts-expect-error
                window.trackJs.track(errorMessage);
            }

            return (
                <CreateMutation mutation={CREATE_LEAD_DISTRIBUTION_AUTOMATION}>
                    {(createLeadDistributionAutomation) => (
                        <UpdateMutation mutation={UPDATE_LEAD_DISTRIBUTION_AUTOMATION}>
                            {(updateLeadDistributionAutomation) => (
                                <DeleteMutation mutation={DELETE_LEAD_DISTRIBUTION_AUTOMATION}>
                                    {(deleteLeadDistributionAutomation) => (
                                        <ConnectedLeadDistributionEditor
                                            {...props}
                                            isLoading={loading}
                                            leadDistributionRule={leadDistributionRule}
                                            // @ts-expect-error
                                            createLeadDistributionAutomation={(payload) =>
                                                createLeadDistributionAutomation({
                                                    variables: {
                                                        leadDistributionAutomation: payload,
                                                    },
                                                    update: (proxy, mutationResult) => {
                                                        const leadDistributionAutomation =
                                                            mutationResult.data &&
                                                            mutationResult.data
                                                                .createLeadDistributionAutomation
                                                                .leadDistributionAutomation;

                                                        if (!leadDistributionAutomation) return;

                                                        // Read the data from our cache for this query.
                                                        const cacheResult:
                                                            | StagesetsForPipelineListQuery
                                                            | null
                                                            | undefined = nutApolloClient.readQuery(
                                                            {
                                                                query: GET_STAGESETS,
                                                            }
                                                        );

                                                        if (!cacheResult) return;

                                                        const stagesets = cacheResult.stagesets.map(
                                                            (stageset) => {
                                                                if (
                                                                    stageset.id ===
                                                                    payload.stagesetId
                                                                ) {
                                                                    stageset.leadDistributionAutomation =
                                                                        {
                                                                            __typename:
                                                                                'LeadDistributionAutomation',
                                                                            id: leadDistributionAutomation.id,
                                                                        };
                                                                }

                                                                return stageset;
                                                            }
                                                        );

                                                        // Write our data back to the cache.
                                                        proxy.writeQuery({
                                                            query: GET_STAGESETS,
                                                            data: {
                                                                stagesets: stagesets,
                                                            },
                                                        });
                                                    },
                                                })
                                            }
                                            // @ts-expect-error
                                            updateLeadDistributionAutomation={(payload) =>
                                                updateLeadDistributionAutomation({
                                                    variables: {
                                                        leadDistributionAutomation: payload,
                                                    },
                                                })
                                            }
                                            // @ts-expect-error
                                            deleteLeadDistributionAutomation={(payload) =>
                                                deleteLeadDistributionAutomation({
                                                    variables: {
                                                        leadDistributionAutomation: payload,
                                                    },
                                                    update: (proxy) => {
                                                        // Read the data from our cache for this query.
                                                        const cacheResult:
                                                            | StagesetsForPipelineListQuery
                                                            | null
                                                            | undefined = nutApolloClient.readQuery(
                                                            {
                                                                query: GET_STAGESETS,
                                                            }
                                                        );

                                                        if (!cacheResult) return;

                                                        const stagesets = cacheResult.stagesets.map(
                                                            (stageset) => {
                                                                if (
                                                                    stageset.leadDistributionAutomation &&
                                                                    stageset
                                                                        .leadDistributionAutomation
                                                                        .id === payload.id
                                                                ) {
                                                                    stageset.leadDistributionAutomation =
                                                                        null;
                                                                }

                                                                return stageset;
                                                            }
                                                        );

                                                        // Write our data back to the cache.
                                                        proxy.writeQuery({
                                                            query: GET_STAGESETS,
                                                            data: {stagesets},
                                                        });
                                                    },
                                                })
                                            }
                                        />
                                    )}
                                </DeleteMutation>
                            )}
                        </UpdateMutation>
                    )}
                </CreateMutation>
            );
        }}
    </GetLeadDistribution>
);

function formatLeadDistributionRule(automation: any): LeadDistributionRule {
    const condition = automation.conditions[0];

    // @ts-expect-error
    if (!condition) return EMPTY_RULE;

    const filter = condition.config.filter;

    if (filter.fieldName === 'emailDomain') {
        return {
            criteriaField: filter.fieldName,
            criteriaValues: [String(filter.booleanValue)],
            valueComparator: 'EQUAL',
        };
    }

    return {
        criteriaField: filter.fieldName,
        criteriaValues: filter.relationshipData ? filter.relationshipData : [filter.currencyValue],
        valueComparator: filter.currencyComparator,
        // @ts-expect-error
        criteriaCollection: null,
    };
}
